import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { createLogger } from '@hunting-nature/shared-core/logging';
import { catchError, from, map, of, switchMap } from 'rxjs';

import type { UserConsentItem } from '../../services/consent.rest.service';
import { BrowserService } from '../../../../shared/services/browser.service';
import { ConsentRestService } from '../../services/consent.rest.service';
import { MenuConsentService } from '../../services/menu-consent.service';
import { GeneralActions } from '../../../../shared/redux/general/general.actions';

import { ConsentActions } from './consent.actions';

@Injectable()
export class ConsentEffects {
  // services
  private readonly actions$ = inject(Actions);
  private readonly browserService = inject(BrowserService);
  private readonly consentRestService = inject(ConsentRestService);
  private readonly menuConsentService = inject(MenuConsentService);
  private readonly log = createLogger('ConsentEffects');

  viewConsentDocument$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ConsentActions.viewConsentDocument),
        switchMap(({ documentUrl }) => from(this.browserService.openExternalUrl(documentUrl)))
      ),
    { dispatch: false }
  );

  // Triggered by GeneralActions.appResumed (system lifecycle, dispatched from GeneralEffects).
  // Kept as a separate effect from missingConsentsRequested$ because mixing system lifecycle
  // and user-intent triggers in a single ofType() violates the team's effects convention.
  appResumedConsents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GeneralActions.appResumed),
      switchMap(({ language }) =>
        this.consentRestService.loadUserConsents$(language).pipe(
          map(consents => ConsentActions.appResumedSucceeded({ consents })),
          catchError((error: unknown) => {
            this.log.error('Load consents on app resume failed', { error });
            return of(ConsentActions.appResumedFailed({ error }));
          })
        )
      )
    )
  );

  missingConsentsRequested$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConsentActions.missingConsentsRequested),
      switchMap(({ language }) =>
        this.consentRestService.loadUserConsents$(language).pipe(
          map(consents => ConsentActions.missingConsentsRequestedSucceeded({ consents })),
          catchError((error: unknown) => {
            this.log.error('Load missing consents failed', { error });
            return of(ConsentActions.missingConsentsRequestedFailed({ error }));
          })
        )
      )
    )
  );

  /** Send the updated consent payload to the backend, then navigate or log out via service. */
  submitConsentUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConsentActions.submitConsentUpdate),
      map(({ consents, mode, language }) => ({
        payload: consents.map(({ id, versionId, consented }: UserConsentItem) => ({ id, versionId, consented })),
        mandatoryRevoked: consents.some((c: UserConsentItem) => !c.optional && !c.consented),
        mode,
        language,
      })),
      switchMap(({ payload, mandatoryRevoked, mode, language }) =>
        this.menuConsentService.submitAndNavigate$(payload, language, mode, mandatoryRevoked).pipe(
          map(({ mandatoryRevoked: revoked }) => ConsentActions.submitConsentUpdateSucceeded({ mandatoryRevoked: revoked })),
          catchError((error: unknown) => {
            this.log.error('Submit consent update failed', { error });
            return of(ConsentActions.submitConsentUpdateFailed({ error }));
          })
        )
      )
    )
  );
}
